import { supabase } from '../supabase';
import { TAX_BILL_STATUS } from '../constants/statuses';
import { DOC_KEYS } from './docVocab';
import { dropEmptyLines } from './billLines';

// Shared "create a submitted/flagged tax bill" — the SINGLE write path for BOTH the Document Scanner and the
// manual tax-section entry point, so the two can't drift. Lifted VERBATIM from DocumentScanner.persistBill's DB
// writes. Does, in order:
//   1. supersede-on-redo guard: find prior LIVE bills for (parcel, collector, installment, tax_year); BLOCK if a
//      terminal one (approved/transmitted/filed) exists; else collect them to supersede.
//   2. optional parcel_documents link (parcel↔file) when a fileExchangeId is provided.
//   3. tax_bills insert (status submitted/flagged).
//   4. supersede the prior live rows → exactly one live bill per key.
//   5. tax_bill_parcels insert (the per-parcel transmittal line).
//   6. parcel_collectors account-number write-back (fill only when blank).
// Returns { billId, superseded }. THROWS on a hard failure (terminal block, tax_bills/tax_bill_parcels insert).
// The caller owns: file_exchange CREATION (scan uploads the PDF; manual attaches later) and the activity log.
//
// `bill` carries the already-parsed tax_bills financial/detail fields (numbers parsed, strings trimmed) so this
// helper stays pure DB plumbing — the scanner parses from billForm, the manual form parses from its inputs.
export async function submitTaxBill({
  status, note,
  propertyId, parcelId, collectorId, taxYear, billType, installmentNumber, supplementalSeq = null, submittedById,
  bill,                       // { account_number, due_date, delinquent_date, value_year, discount_available, discount_amount,
                              //   discount_date, market_value, taxable_value, total_tax_rate, total_ad_valorem,
                              //   total_non_ad_valorem, penalty_fees, total_bill_amount, pay_to_name,
                              //   pay_to_address1, pay_to_address2, pay_to_city, pay_to_state, pay_to_zip,
                              //   jurisdiction_rates, extracted_fields,
                              //   billed_amount, paid_at, paid_amount, paid_source, discount_derived, discount_schedule,
                              //   needs_review, review_note, paid_proof, paid_note, paid_recorded_by }
                              //   ← docs/paid-bills-report.md, docs/payment-status-gap.md; utils/billPayment
  fileExchangeId = null,
  tbp,                        // { market_value, total_ad_valorem, total_non_ad_valorem, amount_due }
  docLink = null,            // { type_key, folder_key, year_subfolder } — only used when fileExchangeId set
}) {
  // installment_number: numbered installments only; Supplemental/Delinquent → null. Prefer an explicit number,
  // else derive from "Installment N" (matches the scanner's reliable integer link to the expected installment).
  const instNum = installmentNumber != null ? installmentNumber
    : (() => { const m = /^Installment\s+(\d+)/.exec(billType || ''); return m ? parseInt(m[1], 10) : null; })();

  // ── 1. Supersede-on-redo guard. Only when the full key is present (a null parcel/collector/tax_year must NOT
  //    supersede across parcels or years).
  //
  // ⛔ AN OFF-SCHEDULE BILL NEEDS MORE THAN `.is('installment_number', null)`. This guard used to stop there,
  //    which made EVERY off-schedule bill for one parcel-collector-year the same bill: a CA supplemental has
  //    its OWN two installments (05/31 and 09/30), both carrying installment_number NULL, so processing the
  //    second would have found the first and SUPERSEDED it. Silent data loss, and the reason a parcel could
  //    not hold a complete supplemental at all. The key is now (parcel, collector, year, installment_number,
  //    bill_type, supplemental_seq) — bill_type separates a Supplemental from a Delinquent, supplemental_seq
  //    separates that supplemental's own installments.
  const canSupersede = !!(parcelId && collectorId && taxYear);
  let priorLiveIds = [];
  if (canSupersede) {
    let sq = supabase.from('tax_bills').select('id,status')
      .eq('parcel_id', parcelId).eq('collector_id', collectorId).eq('tax_year', taxYear)
      .is('superseded_at', null);
    sq = (instNum == null) ? sq.is('installment_number', null) : sq.eq('installment_number', instNum);
    if (instNum == null) {
      sq = billType ? sq.eq('bill_type', billType) : sq.is('bill_type', null);
      sq = supplementalSeq == null ? sq.is('supplemental_seq', null) : sq.eq('supplemental_seq', supplementalSeq);
    }
    const { data: liveBills, error: liveErr } = await sq;
    if (liveErr) throw liveErr;
    const term = (liveBills || []).find(b => [TAX_BILL_STATUS.APPROVED, TAX_BILL_STATUS.TRANSMITTED, TAX_BILL_STATUS.FILED].includes(b.status));
    if (term) throw new Error(`A bill for this parcel/collector/installment is already ${term.status} — cannot supersede; resolve that bill first.`);
    priorLiveIds = (liveBills || []).map(b => b.id);
  }

  // ── 2. Link the doc to the parcel (parcel↔file), same as intake filing — only when a document is provided.
  if (fileExchangeId && parcelId) {
    await supabase.from('parcel_documents').upsert({
      // Keys only (doc_types / doc_folders); the database trigger fills file_type / folder_name from them.
      parcel_id: parcelId, file_exchange_id: fileExchangeId, type_key: docLink?.type_key || DOC_KEYS.taxBill,
      folder_key: docLink?.folder_key || DOC_KEYS.taxBills, year_subfolder: docLink?.year_subfolder || null, property_id: propertyId,
    }, { onConflict: 'file_exchange_id' }).then(() => {}, (e) => console.warn('[submitTaxBill] parcel_documents:', e));
  }

  // Does this bill state a discount is available? Anything but an explicit Yes means no discount may be stored.
  const discountAllowed = String(bill.discount_available || '').trim().toLowerCase() === 'yes';
  if (!discountAllowed && bill.discount_amount != null && Number(bill.discount_amount) !== 0) {
    console.warn('[submitTaxBill] dropped a discount on a bill flagged not-available:',
      { parcelId, collectorId, taxYear, instNum, discount_available: bill.discount_available, discount_amount: bill.discount_amount });
  }

  // ── 3. tax_bills insert.
  const { data: billData, error: tbErr } = await supabase.from('tax_bills').insert({
    property_id: propertyId,
    parcel_id: parcelId || null,
    tax_year: taxYear || null,
    collector_id: collectorId || null,
    account_number: (bill.account_number || '').trim() || null,
    status,
    submitted_by: submittedById || null,
    submitted_at: new Date().toISOString(),
    bill_type: billType || null,
    installment_number: instNum,
    // Only meaningful off-schedule; a scheduled bill's own sequence IS its installment_number.
    supplemental_seq: instNum == null ? (supplementalSeq ?? null) : null,
    due_date: bill.due_date || null,
    delinquent_date: bill.delinquent_date || null,   // manually entered; the extractor never supplies it
    value_year: bill.value_year || null,
    discount_available: bill.discount_available || null,
    // ⛔ A DISCOUNT IS IMPOSSIBLE ON A BILL THAT SAYS THERE ISN'T ONE. Nine CA bills reached 'filed'/'transmitted'
    // carrying a discount_amount while discount_available was 'No' — the scanner wrote whatever the extractor put
    // in the field, and nothing ever compared the two. The values were a scaled copy of the amount due (the
    // extractor returning a percentage of the total, then moneyNum's trailing-3-digit thousands heuristic
    // multiplying a 3-decimal value by a further 1000: 39,715.115 → 39715115).
    // Dropped, not rejected: refusing the whole submit would block a TS over a field the bill says is irrelevant.
    // Mirrored by a DB CHECK (2026-08-19_tax_bills_discount_guard.sql) so a future writer cannot bypass this.
    discount_amount: discountAllowed ? (bill.discount_amount ?? null) : null,
    discount_date: bill.discount_date || null,
    market_value: bill.market_value ?? null,
    taxable_value: bill.taxable_value ?? null,
    total_tax_rate: bill.total_tax_rate ?? null,
    total_ad_valorem: bill.total_ad_valorem ?? null,
    total_non_ad_valorem: bill.total_non_ad_valorem ?? null,
    penalty_fees: bill.penalty_fees ?? null,
    total_bill_amount: bill.total_bill_amount ?? null,   // AMOUNT DUE as printed ($0 on a paid bill)
    // The full tax, and paid / review state (2026-10-08_tax_bills_paid_and_billed). Absent from a caller → null/false.
    billed_amount: bill.billed_amount ?? null,
    paid_at: bill.paid_at || null,
    paid_amount: bill.paid_amount ?? null,
    paid_source: bill.paid_source || null,
    // How the payment is known and who recorded it (2026-10-13_tax_bills_payment_proof). paid_proof needs paid_source.
    paid_proof: bill.paid_source ? (bill.paid_proof || null) : null,
    paid_note: bill.paid_source ? (bill.paid_note || null) : null,
    paid_recorded_by: bill.paid_source ? (bill.paid_recorded_by || null) : null,
    paid_recorded_at: bill.paid_source && bill.paid_recorded_by ? new Date().toISOString() : null,
    discount_derived: !!bill.discount_derived,
    discount_schedule: bill.discount_schedule || null,
    needs_review: !!bill.needs_review,
    review_note: bill.review_note || null,
    pay_to_name: bill.pay_to_name || null,
    pay_to_address1: bill.pay_to_address1 || null,
    pay_to_address2: bill.pay_to_address2 || null,
    pay_to_city: bill.pay_to_city || null,
    pay_to_state: bill.pay_to_state || null,
    pay_to_zip: bill.pay_to_zip || null,
    file_exchange_id: fileExchangeId,
    jurisdiction_rates: dropEmptyLines(bill.jurisdiction_rates) || null,   // never an empty row (billLines)
    extracted_fields: bill.extracted_fields || null,
    rejection_note: note || null,
  }).select('id').single();
  if (tbErr) throw tbErr;

  // ── 4. Supersede prior live rows → exactly one live bill per key.
  if (canSupersede && priorLiveIds.length) {
    const { error: supErr } = await supabase.from('tax_bills')
      .update({ superseded_at: new Date().toISOString(), superseded_by: billData.id })
      .in('id', priorLiveIds);
    if (supErr) console.error('[submitTaxBill] supersede prior bills:', supErr);
  }

  // ── 5. tax_bill_parcels — the per-parcel transmittal line.
  if (parcelId) {
    const { error: tbpErr } = await supabase.from('tax_bill_parcels').insert({
      tax_bill_id: billData.id, parcel_id: parcelId,
      market_value: tbp?.market_value ?? null,
      total_ad_valorem: tbp?.total_ad_valorem ?? null,
      total_non_ad_valorem: tbp?.total_non_ad_valorem ?? null,
      amount_due: tbp?.amount_due ?? null,
    });
    if (tbpErr) throw tbpErr;
  }

  // ── 6. Collector account # write-back → the (parcel, collector, year) link. Fill only when blank; best-effort.
  const billAcct = (bill.account_number || '').trim();
  if (billAcct && parcelId && collectorId) {
    const { data: pcRow, error: pcSelErr } = await supabase.from('parcel_collectors')
      .select('id,account_number').eq('parcel_id', parcelId).eq('collector_id', collectorId).eq('tax_year', taxYear).maybeSingle();
    if (pcSelErr) console.warn('[submitTaxBill] parcel_collectors lookup:', pcSelErr);
    else if (pcRow && !(pcRow.account_number || '').trim()) {
      const { error: pcUpErr } = await supabase.from('parcel_collectors').update({ account_number: billAcct }).eq('id', pcRow.id);
      if (pcUpErr) console.warn('[submitTaxBill] parcel_collectors account_number fill:', pcUpErr);
    }
  }

  return { billId: billData.id, superseded: priorLiveIds };
}
